/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled';
import * as color from '@constant/color';
import { IAdminSourceData } from '@datatype/admin/api-type';
import ExpressionSourceItem from '@component/admin/expression/expression-home/expression-source-list/expression-source-item';
import Spinner from '@design-system/spinner';

interface IProps {
  sources: IAdminSourceData[];
  selectedSourceKey: string | null;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  isError: boolean;
  handleSelect: (source: IAdminSourceData) => void;
  handleDelete?: (source: IAdminSourceData) => void;
  deletingSourceKey?: string | null;
  handleRetry: () => void;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
}

const List = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 8px;
`;

const Loader = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;

const NextLoader = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 0;
`;

const Empty = styled.div`
  padding: 40px 0;
  text-align: center;
  color: ${color.BLACK_5};
`;

const Sentinel = styled.div`
  height: 1px;
`;

const ExpressionSourceList: React.FC<IProps> = ({
  sources,
  selectedSourceKey,
  isLoading,
  isFetchingNextPage,
  isError,
  handleSelect,
  handleDelete = () => undefined,
  deletingSourceKey = null,
  handleRetry,
  sentinelRef,
}) => {
  if (isLoading) {
    return (
      <Loader>
        <Spinner size='32px' color={color.BROWN_1} />
      </Loader>
    );
  }
  if (isError) {
    return (
      <Empty>
        데이터를 불러오지 못했습니다.{' '}
        <span
          style={{ color: color.BROWN_1, cursor: 'pointer', textDecoration: 'underline' }}
          onClick={handleRetry}>
          다시 시도
        </span>
      </Empty>
    );
  }
  if (sources.length === 0) {
    return <Empty>소스가 없습니다.</Empty>;
  }

  const SourceItems = sources.map((source) => (
    <ExpressionSourceItem
      key={source.key}
      source={source}
      isSelected={selectedSourceKey === source.key}
      handleClick={() => handleSelect(source)}
      handleDelete={() => handleDelete(source)}
      isDeleting={deletingSourceKey === source.key}
    />
  ));

  return (
    <>
      <List>{SourceItems}</List>
      {isFetchingNextPage && (
        <NextLoader>
          <Spinner size='24px' color={color.BROWN_1} />
        </NextLoader>
      )}
      <Sentinel ref={sentinelRef} />
    </>
  );
};

export default ExpressionSourceList;
